import { formatDateForDisplay, getLocalizedTargetRepr } from "@/shared/utils";
import type { Locale } from "@/shared/utils";
import type { AuditEntry, AuditFieldChange } from "@/shared/types/audit";
import type { AuditPdfRow } from "./generateAuditPdf";

const ACTION_LABEL_MAP: Record<string, string> = {
  "task.created": "history.actionCategory.task.created",
  "task.updated": "history.actionCategory.task.updated",
  "task.status_changed": "history.actionCategory.task.status_changed",
  "task.deleted": "history.actionCategory.task.deleted",
  "taskcomment.created": "history.actionCategory.taskcomment.created",
  "taskcomment.deleted": "history.actionCategory.taskcomment.deleted",
  "project.created": "history.actionCategory.project.created",
  "project.updated": "history.actionCategory.project.updated",
};

function isFieldChange(value: unknown): value is AuditFieldChange {
  if (!value || typeof value !== "object") return false;
  return "old" in value && "new" in value;
}

function formatChangeValue(value: unknown, displayValue?: string): string {
  if (displayValue) return displayValue;
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function formatChanges(entry: AuditEntry, t: (key: string) => string): string {
  if (!entry.changes) return "";

  if ("snapshot" in entry.changes && entry.changes.snapshot) {
    return "";
  }

  const fields = Object.entries(entry.changes).filter(
    ([key]) => key !== "review_action" && key !== "snapshot" && key !== "position",
  );

  if (fields.length === 0) return "";

  return fields
    .filter(([, change]) => isFieldChange(change))
    .map(([field, change]) => {
      const fc = change as AuditFieldChange;
      const label = t(`history.fieldLabel.${field}`);
      const fieldLabel = label.startsWith("history.fieldLabel.") ? field : label;
      const oldVal = formatChangeValue(fc.old, fc.old_display);
      const newVal = formatChangeValue(fc.new, fc.new_display);
      return `${fieldLabel}: ${oldVal} -> ${newVal}`;
    })
    .join("; ");
}

export function formatAuditRowsForPdf(
  entries: AuditEntry[],
  t: (key: string) => string,
  locale: Locale,
): AuditPdfRow[] {
  return entries.map((entry) => {
    const dateStr = formatDateForDisplay(entry.timestamp, true);
    const actor = entry.actor_type !== "user" ? t("history.systemActor") : entry.actor_name;
    const actionKey = ACTION_LABEL_MAP[entry.action] ?? "history.unknownAction";
    const action = t(actionKey);
    const target = getLocalizedTargetRepr(entry.target_repr, locale);
    const changes = formatChanges(entry, t);

    return {
      cells: [dateStr, actor, action, target, changes],
    };
  });
}
